// =============================================================
// WAVE 4 — Executors & teams (performers = users).
//
// Ports the prototype SecExecutors split-panel over the REAL /users +
// /user-groups. Performers ARE users; the left rail lists them, the right card
// is the detail/edit panel. Create via POST /users, edit via PATCH /users/:id,
// the active toggle writes { isActive } via PATCH.
//
// HONEST API NOTES — the live `users` schema (apps/server/src/db/schema.ts)
// carries only: name, email, username, lang, departmentId, groupIds[], isActive.
// So, relative to the prototype's mock EXECUTORS, we OMIT the fields the schema
// does not have rather than fake them:
//   - in-house vs contractor split  → NO `contractor`/`ext` column on users.
//   - CC / BCC dispatch addresses    → NO cc/bcc columns on users.
//   - "done count"                   → not derivable from users alone (would
//                                       need a per-user completed-task rollup the
//                                       list endpoint does not provide).
// Group/role is resolved from groupIds → /user-groups names. Write affordances
// are hidden without admin:write (the server is still authoritative).
// =============================================================

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useCollection, useCreateRow, useUpdateRow, type Row } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";

interface PerformerRow extends Row {
  email?: string;
  username?: string;
  groupIds?: string[];
  isActive?: boolean;
}

/** Initials from a display name (max 2 letters) for the avatar chip. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function Executors() {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast } = useChrome();
  const canWrite = can("admin", "write");

  const usersQ = useCollection<PerformerRow>("users");
  const groupsQ = useCollection("user-groups");
  const create = useCreateRow("users");
  const update = useUpdateRow("users");

  const users = useMemo(() => usersQ.data ?? [], [usersQ.data]);
  const groups = groupsQ.data ?? [];
  const groupName = (ids: unknown): string => {
    const arr = Array.isArray(ids) ? (ids as string[]) : [];
    const g = groups.find((x) => arr.includes(x.id));
    return g ? String(g.name) : t("executors.noGroup");
  };

  const [selId, setSelId] = useState<string | null>(null);
  useEffect(() => {
    if (!selId && users[0]) setSelId(users[0].id);
  }, [users, selId]);
  const selected = users.find((u) => u.id === selId) ?? null;

  // Detail edit buffer (seeded from the selected row; reset on selection change).
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  useEffect(() => {
    setEditName(selected ? String(selected.name ?? "") : "");
    setEditEmail(selected ? String(selected.email ?? "") : "");
  }, [selId, selected]);
  const dirty =
    selected != null &&
    (editName !== String(selected.name ?? "") || editEmail !== String(selected.email ?? ""));

  // Add-performer inline form.
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const onSave = async () => {
    if (!selected || !editName.trim()) return;
    try {
      await update.mutateAsync({
        id: selected.id,
        body: { name: editName.trim(), email: editEmail.trim() || null },
      });
      toast(t("executors.saved", { name: editName.trim() }), "success");
    } catch {
      toast(t("executors.saveErr"), "error");
    }
  };

  const onToggleActive = async (u: PerformerRow) => {
    const next = !u.isActive;
    try {
      await update.mutateAsync({ id: u.id, body: { isActive: next } });
      toast(t(next ? "executors.activated" : "executors.deactivated", { name: String(u.name) }), "info");
    } catch {
      toast(t("executors.saveErr"), "error");
    }
  };

  const onAdd = async () => {
    if (!newName.trim() || !newUsername.trim()) return;
    try {
      const created = await create.mutateAsync({
        name: newName.trim(),
        username: newUsername.trim(),
        ...(newEmail.trim() ? { email: newEmail.trim() } : {}),
        isActive: true,
      });
      toast(t("executors.created", { name: newName.trim() }), "success");
      setShowAdd(false);
      setNewName("");
      setNewUsername("");
      setNewEmail("");
      if ((created as { id?: string }).id) setSelId((created as { id: string }).id);
    } catch {
      toast(t("executors.createErr"), "error");
    }
  };

  return (
    <div>
      <h2 className="d-h2">{t("executors.title")}</h2>
      <p className="d-sub">{t("executors.sub")}</p>

      <div className="exec-split">
        <div className="exec-list" data-testid="exec-list">
          {usersQ.isLoading && <Skeleton variant="text" count={4} />}
          {!usersQ.isLoading &&
            users.map((u) => (
              <button
                key={u.id}
                className={`exec-item ${selId === u.id ? "sel" : ""} ${u.isActive ? "" : "inactive"}`}
                data-testid={`exec-item-${u.id}`}
                aria-pressed={selId === u.id}
                onClick={() => setSelId(u.id)}
              >
                <span className="av">{initials(String(u.name))}</span>
                <span className="en">
                  {String(u.name)}
                  <small>{groupName(u.groupIds)}</small>
                </span>
                <span className="stt" title={u.isActive ? t("executors.active") : t("executors.inactive")} />
              </button>
            ))}
          {!usersQ.isLoading && users.length === 0 && (
            <EmptyState icon={ICONS.users} title={t("executors.empty")} compact />
          )}
          {canWrite && !usersQ.isLoading && (
            <Btn
              variant="secondary"
              size="sm"
              data-testid="exec-add-open"
              style={{ width: "100%", marginTop: 6 }}
              onClick={() => setShowAdd((v) => !v)}
            >
              <Icon d={ICONS.plus} size={16} />
              {t("executors.add")}
            </Btn>
          )}
        </div>

        <div className="d-card">
          {showAdd && canWrite ? (
            <>
              <div className="d-card-hd">
                <h3>{t("executors.add")}</h3>
              </div>
              <div className="d-card-body">
                <div className="d-field">
                  <label htmlFor="exec-new-name">{t("executors.field.name")}</label>
                  <div className="field-ctrl">
                    <input
                      id="exec-new-name"
                      data-testid="exec-new-name"
                      placeholder={t("executors.field.namePlaceholder")}
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-field">
                  <label htmlFor="exec-new-username">{t("executors.field.username")}</label>
                  <div className="field-ctrl">
                    <input
                      id="exec-new-username"
                      data-testid="exec-new-username"
                      placeholder="username"
                      value={newUsername}
                      onChange={(e) => setNewUsername(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-field">
                  <label htmlFor="exec-new-email">{t("executors.field.email")}</label>
                  <div className="field-ctrl">
                    <Icon d="M4 4h16v16H4zM4 7l8 6 8-6" size={16} style={{ color: "var(--sys-fg-3)" }} />
                    <input
                      id="exec-new-email"
                      type="email"
                      data-testid="exec-new-email"
                      placeholder="name@iterum.lv"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                  <Btn
                    variant="primary"
                    size="sm"
                    data-testid="exec-add-submit"
                    disabled={!newName.trim() || !newUsername.trim() || create.isPending}
                    onClick={() => void onAdd()}
                  >
                    {t("executors.create")}
                  </Btn>
                  <Btn variant="ghost" size="sm" onClick={() => setShowAdd(false)}>
                    {t("common.cancel")}
                  </Btn>
                </div>
              </div>
            </>
          ) : selected ? (
            <>
              <div className="d-card-hd">
                <h3 data-testid="exec-detail-name">{String(selected.name)}</h3>
                <span className="sp" style={{ flex: 1 }} />
                <span
                  className={`chip-status ${selected.isActive ? "success" : "paused"}`}
                  data-testid="exec-detail-active"
                >
                  <span className="led" />
                  {selected.isActive ? t("executors.active") : t("executors.inactive")}
                </span>
              </div>
              <div className="d-card-body">
                <div className="d-field">
                  <label htmlFor="exec-edit-name">{t("executors.field.name")}</label>
                  <div className="field-ctrl is-filled">
                    <input
                      id="exec-edit-name"
                      data-testid="exec-edit-name"
                      value={editName}
                      disabled={!canWrite}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-field">
                  <label htmlFor="exec-edit-email">{t("executors.field.email")}</label>
                  <div className={`field-ctrl ${editEmail ? "is-filled" : ""}`}>
                    <Icon d="M4 4h16v16H4zM4 7l8 6 8-6" size={16} style={{ color: "var(--sys-fg-3)" }} />
                    <input
                      id="exec-edit-email"
                      type="email"
                      data-testid="exec-edit-email"
                      placeholder="name@iterum.lv"
                      value={editEmail}
                      disabled={!canWrite}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-field">
                  <label>{t("executors.field.group")}</label>
                  <div className="field-ctrl is-filled" style={{ background: "var(--sys-bg-sunken)" }}>
                    <input value={groupName(selected.groupIds)} readOnly data-testid="exec-detail-group" />
                  </div>
                </div>

                <div className="set-row" style={{ borderTop: "1px solid var(--sys-hairline)", borderBottom: 0, marginTop: 6 }}>
                  <div className="sl">
                    <div className="nm">{t("executors.activePerformer")}</div>
                    <div className="ds">{t("executors.activeHint", { name: String(selected.name) })}</div>
                  </div>
                  <button
                    className={`toggle ${selected.isActive ? "on" : ""}`}
                    role="switch"
                    aria-checked={!!selected.isActive}
                    aria-label={t("executors.activePerformer")}
                    data-testid="exec-toggle-active"
                    disabled={!canWrite}
                    onClick={() => void onToggleActive(selected)}
                  >
                    <span className="knob" />
                  </button>
                </div>

                {canWrite && (
                  <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
                    <Btn
                      variant="primary"
                      size="sm"
                      data-testid="exec-save"
                      disabled={!dirty || !editName.trim() || update.isPending}
                      onClick={() => void onSave()}
                    >
                      {t("executors.save")}
                    </Btn>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="d-card-body">
              <EmptyState icon={ICONS.user} title={t("executors.selectHint")} compact />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
